import React, { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

const CoordinateInputControl = ({ featureGroupRef }) => {
    const map = useMap();
    const controlRef = useRef(null);
    
    useEffect(() => {
        // Only initialize if we have a map and don't already have a control
        if (!map || controlRef.current) return;
        
        try {
            // Create a custom control
            const CoordControl = L.Control.extend({
                options: {
                    position: 'bottomright'
                },
                
                onAdd: function() {
                    // Create container
                    const container = L.DomUtil.create('div', 'leaflet-control leaflet-bar');
                    
                    // Create button
                    const button = L.DomUtil.create('a', 'coordinate-button', container);
                    button.href = '#';
                    button.title = 'Add point by coordinates';
                    button.innerHTML = '<span style="font-weight: bold;">XY</span>';
                    button.style.display = 'flex';
                    button.style.alignItems = 'center';
                    button.style.justifyContent = 'center';
                    button.style.width = '30px';
                    button.style.height = '30px';
                    
                    // Create form container (initially hidden)
                    const formContainer = L.DomUtil.create('div', 'coordinate-form-container', container);
                    formContainer.style.display = 'none';
                    formContainer.style.position = 'absolute';
                    formContainer.style.bottom = '40px';
                    formContainer.style.right = '0';
                    formContainer.style.backgroundColor = 'white';
                    formContainer.style.padding = '10px';
                    formContainer.style.borderRadius = '4px';
                    formContainer.style.boxShadow = '0 1px 5px rgba(0,0,0,0.65)';
                    formContainer.style.zIndex = '1000';
                    formContainer.style.width = '180px';
                    
                    // Create form
                    const form = L.DomUtil.create('form', '', formContainer);
                    
                    // Add latitude input
                    const latDiv = L.DomUtil.create('div', '', form);
                    latDiv.style.marginBottom = '8px';
                    
                    const latLabel = L.DomUtil.create('label', '', latDiv);
                    latLabel.htmlFor = 'lat-input';
                    latLabel.innerHTML = 'Latitude:';
                    latLabel.style.display = 'block';
                    latLabel.style.marginBottom = '3px';
                    latLabel.style.fontSize = '12px';
                    
                    const latInput = L.DomUtil.create('input', '', latDiv);
                    latInput.id = 'lat-input';
                    latInput.type = 'text';
                    latInput.placeholder = 'e.g. 41.8859';
                    latInput.style.width = '100%';
                    latInput.style.padding = '4px';
                    latInput.style.boxSizing = 'border-box';
                    
                    // Add longitude input
                    const lngDiv = L.DomUtil.create('div', '', form);
                    lngDiv.style.marginBottom = '8px';
                    
                    const lngLabel = L.DomUtil.create('label', '', lngDiv);
                    lngLabel.htmlFor = 'lng-input';
                    lngLabel.innerHTML = 'Longitude:';
                    lngLabel.style.display = 'block';
                    lngLabel.style.marginBottom = '3px';
                    lngLabel.style.fontSize = '12px';
                    
                    const lngInput = L.DomUtil.create('input', '', lngDiv);
                    lngInput.id = 'lng-input';
                    lngInput.type = 'text';
                    lngInput.placeholder = 'e.g. 12.4428';
                    lngInput.style.width = '100%';
                    lngInput.style.padding = '4px';
                    lngInput.style.boxSizing = 'border-box';
                    
                    // Add error message div
                    const errorDiv = L.DomUtil.create('div', '', formContainer);
                    errorDiv.style.color = 'red';
                    errorDiv.style.fontSize = '12px';
                    errorDiv.style.marginBottom = '8px';
                    errorDiv.style.display = 'none';
                    
                    // Add buttons
                    const buttonDiv = L.DomUtil.create('div', '', form);
                    buttonDiv.style.display = 'flex';
                    buttonDiv.style.justifyContent = 'space-between';
                    
                    const addButton = L.DomUtil.create('button', '', buttonDiv);
                    addButton.type = 'submit';
                    addButton.innerHTML = 'Add Point';
                    addButton.style.padding = '5px 10px';
                    
                    const cancelButton = L.DomUtil.create('button', '', buttonDiv);
                    cancelButton.type = 'button';
                    cancelButton.innerHTML = 'Cancel';
                    cancelButton.style.padding = '5px 10px';
                    
                    // Add event handlers
                    const toggleForm = function(e) {
                        e.preventDefault();
                        e.stopPropagation();
                        formContainer.style.display = formContainer.style.display === 'none' ? 'block' : 'none';
                        if (formContainer.style.display === 'block') {
                            latInput.focus();
                        }
                    };
                    
                    const hideForm = function() {
                        formContainer.style.display = 'none';
                        latInput.value = '';
                        lngInput.value = '';
                        errorDiv.style.display = 'none';
                    };
                    
                    const handleSubmit = function(e) {
                        e.preventDefault();
                        e.stopPropagation();
                        
                        // Get values
                        const lat = parseFloat(latInput.value);
                        const lng = parseFloat(lngInput.value);
                        
                        // Validate inputs
                        if (isNaN(lat) || isNaN(lng)) {
                            errorDiv.innerHTML = 'Please enter valid numbers';
                            errorDiv.style.display = 'block';
                            return;
                        }
                        
                        if (lat < -90 || lat > 90) {
                            errorDiv.innerHTML = 'Latitude must be between -90 and 90';
                            errorDiv.style.display = 'block';
                            return;
                        }
                        
                        if (lng < -180 || lng > 180) {
                            errorDiv.innerHTML = 'Longitude must be between -180 and 180';
                            errorDiv.style.display = 'block';
                            return;
                        }
                        
                        // Create marker at coordinates
                        const coords = [lat, lng];
                        
                        // Clear existing layers
                        if (featureGroupRef && featureGroupRef.current) {
                            featureGroupRef.current.clearLayers();
                        }
                        
                        // Add marker to map
                        const marker = L.marker(coords);
                        
                        if (featureGroupRef && featureGroupRef.current) {
                            featureGroupRef.current.addLayer(marker);
                            map.fire(L.Draw.Event.CREATED, { layer: marker });
                        } else {
                            marker.addTo(map);
                        }
                        
                        // Pan to marker
                        map.setView(coords, 13);
                        
                        // Hide form
                        hideForm();
                    };
                    
                    // Attach event listeners
                    L.DomEvent.on(button, 'click', toggleForm);
                    L.DomEvent.on(form, 'submit', handleSubmit);
                    L.DomEvent.on(cancelButton, 'click', hideForm);
                    
                    // Close form when clicking outside
                    L.DomEvent.on(document, 'click', function(e) {
                        if (!container.contains(e.target)) {
                            hideForm();
                        }
                    });
                    
                    // Prevent map clicks and zooms when interacting with the control
                    L.DomEvent.disableClickPropagation(container);
                    L.DomEvent.disableScrollPropagation(container);
                    
                    return container;
                }
            });
            
            // Create and add control to map
            const coordControl = new CoordControl();
            map.addControl(coordControl);
            controlRef.current = coordControl;
            
        } catch (error) {
            console.error("Error creating coordinate input control:", error);
        }
        
        // Cleanup function
        return () => {
            try {
                if (map && controlRef.current) {
                    map.removeControl(controlRef.current);
                }
            } catch (error) {
                console.error("Error cleaning up coordinate control:", error);
            } finally {
                controlRef.current = null;
            }
        };
    }, [map, featureGroupRef]);
    
    return null;
};

export default CoordinateInputControl;